import About from "./Components/About";
import Contact from "./Components/Contact";
import EducationExperience from "./Components/EducationExperience";
import Footer from "./Components/Footer";
import Home from "./Components/Home";
import Navbar from "./Components/Navbar";
import Projects from "./Components/Projects";
import Techstack from "./Components/Techstack";
import { Box, useColorModeValue, keyframes, useColorMode } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";

const bgAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const App = () => {
    const { colorMode, toggleColorMode } = useColorMode();
    const [isAnimating, setIsAnimating] = useState(false);
    const bgColor = useColorModeValue("white", "gray.900");
    const meshColor1 = useColorModeValue("rgba(59, 130, 246, 0.05)", "rgba(59, 130, 246, 0.1)");
    const meshColor2 = useColorModeValue("rgba(139, 92, 246, 0.05)", "rgba(139, 92, 246, 0.1)");
    const isLight = colorMode === "light";
    // Physical Page Cover Backgrounds
    const sweepBg = useColorModeValue("rgba(240, 244, 248, 0.99)", "rgba(15, 23, 42, 0.99)"); 

    const toggleWithSweep = () => {
        setIsAnimating(true);
        // Switch at exact midpoint of the 1.8s sweep
        const timer = setTimeout(() => {
            toggleColorMode();
            setIsAnimating(false);
        }, 900); 
        return () => clearTimeout(timer);
    };

    // Single-mount animation timing
    const contentVariants = {
        idle: { opacity: 1, scale: 1, y: 0, filter: "blur(0px)" },
        switching: { opacity: 0.6, scale: 0.98, y: -30, filter: "blur(8px)" }
    };
    const contentTransition = { duration: 0.6, ease: [0.22, 1, 0.36, 1] };
    const sweepTransition = { duration: 1.8, ease: [0.19, 1, 0.22, 1] };

    return (
        <Box
            minH="100vh"
            bg={bgColor}
            bgImage={`radial-gradient(at 0% 0%, ${meshColor1} 0, transparent 50%), 
                        radial-gradient(at 50% 0%, ${meshColor2} 0, transparent 50%)`}
            bgSize="200% 200%"
            animation={`${bgAnimation} 15s ease infinite`}
            overflow="hidden"
        >
            <Navbar onToggleTheme={toggleWithSweep} />

            {/* Zero-Refresh Persistent Content */}
            <Box
                as={motion.div}
                initial="idle"
                animate={isAnimating ? "switching" : "idle"}
                variants={contentVariants}
                transition={contentTransition}
            >
                <Home />
                <About />
                <EducationExperience />
                <Techstack />
                <Projects />
                <Contact />
                <Footer />
            </Box>

            {/* Diagonal Reversible Glassmorphism Sweep Overlay */}
            <AnimatePresence>
                {isAnimating && (
                    <Box
                        as={motion.div}
                        key="global-sweep"
                        initial={{ 
                            clipPath: isLight ? "circle(0% at 0% 100%)" : "circle(0% at 100% 0%)",
                            opacity: 0,
                            backdropFilter: "blur(0px)" 
                        }}
                        animate={{ 
                            clipPath: isLight ? "circle(180% at 0% 100%)" : "circle(180% at 100% 0%)",
                            opacity: 1,
                            backdropFilter: "blur(40px)" 
                        }}
                        exit={{ 
                            clipPath: isLight ? "circle(0% at 100% 0%)" : "circle(0% at 0% 100%)",
                            opacity: 0,
                            backdropFilter: "blur(0px)"
                        }}
                        transition={sweepTransition}
                        position="fixed"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        bg={sweepBg}
                        zIndex={999999}
                        pointerEvents="none"
                    />
                )}
            </AnimatePresence>
        </Box>
    );
};

export default App;